// --------------------------------------------------------------
// Created On: 2024-04-05
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import styles from "./ShoppingCart.module.scss";
import ShoppingCartItem from "./ShoppingCartItem/ShoppingCartItem";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import PaintQuantityModal from "./PaintQuantityModal/PaintQuantityModal";
import useRefreshCart from "../../hooks/useRefreshCart";
import deepCopy from "../../utilities/deepCopy";
import findExistingCartItem from "../../utilities/findExistingCartItem";

// Component that contains a list of cart item components and displays the calculated price and discounted price.
export default function ShoppingCart(props: Props): Component {
  const [selectedDeleteCartItemId, setSelectedDeleteCartItemId] = useState<number | null>(null);
  const [selectedPaintCartItemId, setSelectedPaintCartItemId] = useState<number | null>(null);
  const [selectedPaintColor, setSelectedPaintColor] = useState<PaintColor | null>(null);
  const [paintModalErrorMessage, setPaintModalErrorMessage] = useState<string | null>("");
  const [showCombineModal, setShowCombineModal] = useState<boolean>(false);
  const [showDeleteAllItemsModal, setShowDeleteAllItemsModal] = useState<boolean>(false);
  const [selectedUpdateCartItemId, setSelectedUpdateCartItemId] = useState<number | null>(null);
  const [selectedUpdateQuantity, setSelectedUpdateQuantity] = useState<string | null>(null);
  const [selectedUpdatePaintColorId, setSelectedUpdatePaintColorId] = useState<number | null>(null);
  const [selectedUpdateImpellerTrim, setSelectedUpdateImpellerTrim] = useState<string | null>(null);
  const [selectedUpdateImpellerAngle, setSelectedUpdateImpellerAngle] = useState<string | null>(null);
  const [cartQuantityChangedCounter, setCartQuantityChangedCounter] = useState<number>(0);
  const [listPrice, setListPrice] = useState<number | null>(null);
  const [itemCount, setItemCount] = useState<number | null>(null);

  // Refresh the Redux store's cart quantity whenever an item is added to the cart.
  useRefreshCart(() => {
    return cartQuantityChangedCounter > 0;
  }, [cartQuantityChangedCounter]);

  // Update the list price whenever the array of items is changed.
  useEffect(() => {
    let cartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    if (!props.isQuote || props.quoteId === undefined) {
      cartItems = props.items;
    } else {
      cartItems = props.quoteCartItems;
    }
    setListPrice(
      cartItems.reduce(
        (priceSubtotal, { price, quantity }) => (price !== null ? priceSubtotal + price * Number(quantity) : 0),
        0
      )
    );
    setItemCount(cartItems.reduce((quantitySubtotal, { quantity }) => quantitySubtotal + Number(quantity), 0));
  }, [JSON.stringify(props.items), JSON.stringify(props.quoteCartItems), props.isQuote]);

  // Calls the delete cart item prop function and sets deleteCartItemId to null to close modal.
  function handleDeleteCartItem(cartItemId: number | null) {
    props.onDeleteCartItem(cartItemId);
    setSelectedDeleteCartItemId(null);
  }

  // Sets the selected paint cart item id and the selected paint color.
  function handleMultiplePaintItems(cartItemId: number, newPaintColor: PaintColor | null) {
    setSelectedPaintCartItemId(cartItemId);
    setSelectedPaintColor(newPaintColor);
  }

  // Resets the paint color item id and selected paint color on modal close.
  function closePaintColorModal() {
    setSelectedPaintCartItemId(null);
    setSelectedPaintColor(null);
  }

  // Validation function to ensure that a user is not inputting an invalid quantity when attempting to update multiple
  // paint items.
  function validatePaintQuantity(newQuantity: string, currentQuantity: string): boolean {
    if (isNaN(parseInt(newQuantity, 10))) {
      setPaintModalErrorMessage("Quantity must be a valid number.");
      return false;
    } else if (parseInt(newQuantity, 10) > parseInt(currentQuantity, 10)) {
      setPaintModalErrorMessage(`New quantity must not be greater than the current quantity (${currentQuantity})`);
      return false;
    } else {
      closePaintColorModal();
      return true;
    }
  }

  // Wrapper function to call update multiple paint items prop. Validates that the quantity is valid first.
  function updateMultiplePaintItems(quantity: string | null, applyToAllItems: boolean) {
    let shoppingCartItems: CartItem[] | QuoteCartItemShoppingCart[] = [];
    if (props.quoteId === undefined || !props.isQuote) {
      shoppingCartItems = props.items;
    } else {
      shoppingCartItems = props.quoteCartItems;
    }
    const updatedItemIndex = shoppingCartItems.findIndex((item) => item.cartItemId === selectedPaintCartItemId);
    const updatedItem = shoppingCartItems[updatedItemIndex];
    let paintColorId = null;
    if (selectedPaintColor !== null) {
      paintColorId = selectedPaintColor.paintColorId;
    }
    if (applyToAllItems) {
      handleUpdateCartItems(
        updatedItem.cartItemId,
        updatedItem.quantity,
        updatedItem.quantityIsValid,
        paintColorId,
        updatedItem.impellerTrim,
        updatedItem.impellerTrimIsValid,
        updatedItem.impellerAngle,
        updatedItem.impellerAngleIsValid
      );
      closePaintColorModal();
    } else if (selectedPaintCartItemId !== null && quantity !== null) {
      const updatedItemIndex = shoppingCartItems.findIndex((item) => item.cartItemId === selectedPaintCartItemId);
      const updatedItem = shoppingCartItems[updatedItemIndex];
      if (validatePaintQuantity(quantity, updatedItem.quantity)) {
        props.onUpdateMultiplePaintCartItems(selectedPaintCartItemId, paintColorId, quantity);
      }
    }
  }

  // Determines whether a modal needs to appear to inform the user that multiple items will be combined as a result of this update.
  function parseUpdatedCart(
    itemId: number,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    let existingCartItemId: number | null = null;
    if (props.quoteId === undefined || !props.isQuote) {
      const itemsDeepCopy = deepCopy(props.items);
      // Get the item we are updating.
      const updatedItem = itemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
      const updatedItemIndex = itemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);

      if (updatedItem !== undefined && updatedItemIndex > -1) {
        if (quantityIsValid) {
          itemsDeepCopy[updatedItemIndex].quantity = quantity;
        }
        if (impellerTrimIsValid) {
          itemsDeepCopy[updatedItemIndex].impellerTrim = impellerTrim;
        }
        if (impellerAngleIsValid) {
          itemsDeepCopy[updatedItemIndex].impellerAngle = impellerAngle;
        }
        itemsDeepCopy[updatedItemIndex].paintColorId = paintColorId;

        // Check to see if the cart item already exists. Update quantity if so.
        existingCartItemId = findExistingCartItem(itemsDeepCopy[updatedItemIndex], itemsDeepCopy);
      }
    } else {
      const quoteItemsDeepCopy = deepCopy(props.quoteCartItems);
      const previousItem = props.previousQuoteCartItems.find((cartItem) => cartItem.cartItemId === itemId);
      // Get the item we are updating.
      const updatedItem = quoteItemsDeepCopy.find((cartItem) => cartItem.cartItemId === itemId);
      const updatedItemIndex = quoteItemsDeepCopy.findIndex((cartItem) => cartItem.cartItemId === itemId);
      let duplicateItem = undefined;
      let previousItemQuantity = null;

      if (updatedItem !== undefined) {
        duplicateItem = quoteItemsDeepCopy.find(
          (cartItem) => cartItem.partNumber === updatedItem.partNumber && cartItem.cartItemId !== updatedItem.cartItemId
        );
      }

      if (updatedItem !== undefined && updatedItemIndex > -1) {
        if (quoteItemsDeepCopy.length > props.previousQuoteCartItems.length && duplicateItem !== undefined) {
          if (previousItem !== undefined) {
            previousItemQuantity = Number(previousItem.quantity);
          } else {
            previousItemQuantity = Number(updatedItem.quantity) + Number(duplicateItem.quantity);
          }
          const combinedItemQuantity = Number(quantity) + Number(duplicateItem.quantity);
          // If the list of items has grown due to multiple items having the same part number with different paint colors,
          // we need to ensure the quantity of the total sum of parts is still the same.
          if (combinedItemQuantity > previousItemQuantity) {
            quantityIsValid = false;
          } else {
            quantityIsValid = true;
          }
        } else if (props.previousQuoteCartItems.length > quoteItemsDeepCopy.length && duplicateItem === undefined) {
          const previousItemQuantity = updatedItem.quantity;
          const newQuantity = quantity;
          if (Number(newQuantity) > Number(previousItemQuantity)) {
            quantityIsValid = false;
          } else {
            quantityIsValid = true;
          }
        } else {
          if (previousItem !== undefined) {
            previousItemQuantity = previousItem.quantity;
          } else {
            previousItemQuantity = updatedItem.quantity;
          }

          // Get the new quantity.
          const newItemQuantity = quantity;
          if (Number(newItemQuantity) > Number(previousItemQuantity)) {
            quantityIsValid = false;
          } else {
            quantityIsValid = true;
          }
        }
      }

      // If the new quantity is less than or equal to the current item quantity, update the item.
      if (quantityIsValid) {
        quoteItemsDeepCopy[updatedItemIndex].quantity = quantity;
      }
      if (impellerTrimIsValid) {
        quoteItemsDeepCopy[updatedItemIndex].impellerTrim = impellerTrim;
      }
      if (impellerAngleIsValid) {
        quoteItemsDeepCopy[updatedItemIndex].impellerAngle = impellerAngle;
      }
      quoteItemsDeepCopy[updatedItemIndex].paintColorId = paintColorId;

      // Check to see if the cart item already exists. Update quantity if so.
      existingCartItemId = findExistingCartItem(quoteItemsDeepCopy[updatedItemIndex], quoteItemsDeepCopy);
    }

    if (existingCartItemId !== null) {
      setShowCombineModal(true);
      setSelectedUpdateCartItemId(itemId);
      setSelectedUpdateQuantity(quantity);
      setSelectedUpdatePaintColorId(paintColorId);
      setSelectedUpdateImpellerTrim(impellerTrim);
      setSelectedUpdateImpellerAngle(impellerAngle);
    } else {
      if (impellerTrim !== null && impellerTrim !== "" && impellerAngle !== null && impellerAngle !== "") {
        handleUpdateCartItems(
          itemId,
          quantity,
          quantityIsValid,
          paintColorId,
          String(parseFloat(impellerTrim)),
          impellerTrimIsValid,
          String(parseFloat(impellerAngle)),
          impellerAngleIsValid
        );
      } else {
        handleUpdateCartItems(
          itemId,
          quantity,
          quantityIsValid,
          paintColorId,
          impellerTrim,
          impellerTrimIsValid,
          impellerAngle,
          impellerAngleIsValid
        );
      }
    }
  }

  // Wrapper function to validate cart item update before passing state up.
  function handleUpdateCartItems(
    itemId: number | null,
    quantity: string | null,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) {
    if (itemId !== null && quantity !== null) {
      setShowCombineModal(false);
      setPaintModalErrorMessage(null);
      props.onUpdateCartItem(
        itemId,
        quantity,
        quantityIsValid,
        paintColorId,
        impellerTrim,
        impellerTrimIsValid,
        impellerAngle,
        impellerAngleIsValid
      );
    }
  }

  // Delete all items in the cart and refresh the shopping cart quantity.
  function handleDeleteAllCartItems(): void {
    setShowDeleteAllItemsModal(false);
    setCartQuantityChangedCounter((previous) => previous + 1);
    props.onDeleteAllCartItems();
  }

  return (
    <Fragment>
      {props.accordionView ? (
        <div className={`${styles.body}`}>
          {listPrice !== null && listPrice !== 0 && itemCount !== null && itemCount !== 0 ? (
            <div>
              <div className={`${styles.headerAccordion} p-1`}>
                <div className="row align-items-top">
                  <ConfirmModal
                    showModal={selectedDeleteCartItemId !== null}
                    title="Remove cart item"
                    content={"Are you sure that you want to remove this item from the cart?"}
                    yesText="Remove item"
                    noText="Cancel"
                    danger={true}
                    onClose={() => setSelectedDeleteCartItemId(null)}
                    onYes={() => handleDeleteCartItem(selectedDeleteCartItemId)}
                    onNo={() => setSelectedDeleteCartItemId(null)}
                  />
                  <ConfirmModal
                    showModal={showCombineModal}
                    title="Combine Cart Items"
                    content={"This change will result in identical cart items being combined."}
                    yesText="Combine items"
                    noText="Cancel"
                    danger={false}
                    onClose={() => setShowCombineModal(false)}
                    onYes={() =>
                      handleUpdateCartItems(
                        selectedUpdateCartItemId,
                        selectedUpdateQuantity,
                        true,
                        selectedUpdatePaintColorId,
                        selectedUpdateImpellerTrim,
                        true,
                        selectedUpdateImpellerAngle,
                        true
                      )
                    }
                    onNo={() => setShowCombineModal(false)}
                  />
                  <PaintQuantityModal
                    showModal={selectedPaintCartItemId !== null}
                    title="Update Paint Color"
                    content={"How many parts should be painted this color"}
                    yesText="Apply Paint Color"
                    noText="Cancel"
                    danger={false}
                    onClose={() => closePaintColorModal()}
                    onYes={(quantity: string | null, applyToAllItems: boolean) =>
                      updateMultiplePaintItems(quantity, applyToAllItems)
                    }
                    onNo={() => closePaintColorModal()}
                    errorMessage={paintModalErrorMessage}
                  />
                  <div className="container">
                    <ul className={`${styles.cartItemList}`}>
                      {/* ShoppingCartItem for SubmitOrderPage where we are creating a new sales order or new quote. */}
                      {props.quoteId === undefined &&
                        props.items.map((item) => (
                          <ShoppingCartItem
                            key={item.cartItemId}
                            cartItemId={item.cartItemId}
                            name={item.name}
                            partNumber={item.partNumber}
                            paintColorId={item.paintColorId}
                            quantity={item.quantity}
                            quantityIsValid={item.quantityIsValid}
                            units={item.units}
                            price={item.price}
                            weight={item.weight}
                            companyDiscount={props.companyDiscount}
                            impellerTrim={item.impellerTrim}
                            impellerTrimIsValid={item.impellerTrimIsValid}
                            impellerAngle={item.impellerAngle}
                            impellerAngleIsValid={item.impellerAngleIsValid}
                            dateAvailable={item.dateAvailable}
                            isPaintable={item.isPaintable}
                            paintColors={props.paintColors}
                            isQuote={props.isQuote}
                            quoteId={props.quoteId}
                            onUpdateMultiplePaintCartItems={(itemId, newPaintColor) =>
                              handleMultiplePaintItems(itemId, newPaintColor)
                            }
                            onUpdateCartItem={(
                              itemId,
                              quantity,
                              quantityIsValid,
                              paintColorId,
                              impellerTrim,
                              impellerTrimIsValid,
                              impellerAngle,
                              impellerAngleIsValid
                            ) =>
                              parseUpdatedCart(
                                itemId,
                                quantity,
                                quantityIsValid,
                                paintColorId,
                                impellerTrim,
                                impellerTrimIsValid,
                                impellerAngle,
                                impellerAngleIsValid
                              )
                            }
                            onUpdateCartItemDateAvailable={(itemId, dateAvailable) =>
                              props.onUpdateCartItemDateAvailable(itemId, dateAvailable)
                            }
                            onUpdateQuoteCartItemDateAvailable={(itemId, dateAvailable) =>
                              props.onUpdateQuoteCartItemDateAvailable(itemId, dateAvailable)
                            }
                            onDeleteCartItem={() => setSelectedDeleteCartItemId(item.cartItemId)}
                          />
                        ))}
                      {/* ShoppingCartItem for SubmitOrderPage for placing a sales order from an existing quote. */}
                      {props.quoteId !== undefined &&
                        props.quoteCartItems.map((item) => (
                          <ShoppingCartItem
                            key={item.cartItemId}
                            cartItemId={item.cartItemId}
                            name={item.name}
                            partNumber={item.partNumber}
                            paintColorId={item.paintColorId}
                            quantity={item.quantity}
                            quantityIsValid={item.quantityIsValid}
                            units={item.units}
                            price={item.price}
                            weight={item.weight}
                            companyDiscount={props.companyDiscount}
                            impellerTrim={item.impellerTrim}
                            impellerTrimIsValid={item.impellerTrimIsValid}
                            impellerAngle={item.impellerAngle}
                            impellerAngleIsValid={item.impellerAngleIsValid}
                            dateAvailable={item.dateAvailable}
                            isPaintable={item.isPaintable}
                            paintColors={props.paintColors}
                            isQuote={props.isQuote}
                            quoteId={props.quoteId}
                            onUpdateMultiplePaintCartItems={(itemId, newPaintColor) =>
                              handleMultiplePaintItems(itemId, newPaintColor)
                            }
                            onUpdateCartItem={(
                              itemId,
                              quantity,
                              quantityIsValid,
                              paintColorId,
                              impellerTrim,
                              impellerTrimIsValid,
                              impellerAngle,
                              impellerAngleIsValid
                            ) =>
                              parseUpdatedCart(
                                itemId,
                                quantity,
                                quantityIsValid,
                                paintColorId,
                                impellerTrim,
                                impellerTrimIsValid,
                                impellerAngle,
                                impellerAngleIsValid
                              )
                            }
                            onUpdateCartItemDateAvailable={(itemId, dateAvailable) =>
                              props.onUpdateCartItemDateAvailable(itemId, dateAvailable)
                            }
                            onUpdateQuoteCartItemDateAvailable={(itemId, dateAvailable) =>
                              props.onUpdateQuoteCartItemDateAvailable(itemId, dateAvailable)
                            }
                            onDeleteCartItem={() => setSelectedDeleteCartItemId(item.cartItemId)}
                          />
                        ))}
                    </ul>
                  </div>
                  <div className="pt-3">
                    {props.companyDiscount !== null ? (
                      <ul className={styles.priceListAccordion} data-test="total-price-list">
                        {itemCount > 1 ? (
                          <li className={styles.priceCategory}>
                            Subtotal ({itemCount} items):{""}
                            <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                              $
                              {listPrice.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </li>
                        ) : (
                          <li className={styles.priceCategory}>
                            Subtotal ({itemCount} item):{""}
                            <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                              $
                              {listPrice.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </li>
                        )}
                        <li className={styles.priceCategory}>
                          {props.companyName} Discount:{" "}
                          <span className={styles.priceValue}>{props.companyDiscount}%</span>
                        </li>
                        <li className={styles.priceCategory}>
                          Total after discount:{" "}
                          <span className={styles.priceValue}>
                            $
                            {(listPrice - listPrice * (props.companyDiscount / 100)).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </li>
                      </ul>
                    ) : (
                      <ul className={styles.priceListAccordion} data-test="price-list">
                        <li className={styles.priceCategory}>
                          Total:{" "}
                          <span className={styles.priceValue}>
                            ${listPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div> //If the cart is empty display a message
          ) : (
            <div>
              <div className={`${styles.headerAccordion} p-3`}>
                <div className="row align-items-top">
                  <div className="col">
                    <div className={`${styles.title} row`}>
                      <div className="col">Shopping Cart</div>
                    </div>
                  </div>
                </div>
                <p className={styles.emptyShoppingCart}>Your shopping cart is empty.</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <Fragment>
          {itemCount !== null && itemCount !== 0 ? (
            <div className={`${styles.cartContainer} container mt-4`}>
              <div className="row align-items-top">
                <div className="col">
                  <ConfirmModal
                    showModal={selectedDeleteCartItemId !== null}
                    title="Remove cart item"
                    content={"Are you sure that you want to remove this item from the cart?"}
                    yesText="Remove item"
                    noText="Cancel"
                    danger={true}
                    onClose={() => {
                      setSelectedDeleteCartItemId(null);
                    }}
                    onYes={() => handleDeleteCartItem(selectedDeleteCartItemId)}
                    onNo={() => setSelectedDeleteCartItemId(null)}
                  />
                  <ConfirmModal
                    showModal={showCombineModal}
                    title="Combine Cart Items"
                    content={"This change will result in identical cart items being combined."}
                    yesText="Combine items"
                    noText="Cancel"
                    danger={false}
                    onClose={() => setShowCombineModal(false)}
                    onYes={() =>
                      handleUpdateCartItems(
                        selectedUpdateCartItemId,
                        selectedUpdateQuantity,
                        true,
                        selectedUpdatePaintColorId,
                        selectedUpdateImpellerTrim,
                        true,
                        selectedUpdateImpellerAngle,
                        true
                      )
                    }
                    onNo={() => setShowCombineModal(false)}
                  />
                  <ConfirmModal
                    showModal={showDeleteAllItemsModal}
                    title="Delete All Cart Items"
                    content="Are you sure you want to remove all items from your cart?"
                    yesText="Delete All Cart Items"
                    noText="Cancel"
                    danger={true}
                    onClose={() => setShowDeleteAllItemsModal(false)}
                    onYes={() => handleDeleteAllCartItems()}
                    onNo={() => setShowDeleteAllItemsModal(false)}
                  />
                  <PaintQuantityModal
                    showModal={selectedPaintCartItemId !== null}
                    title="Update Paint Color"
                    content={"How many parts should be painted this color"}
                    yesText="Apply Paint Color"
                    noText="Cancel"
                    danger={false}
                    onClose={() => closePaintColorModal()}
                    onYes={(quantity: string | null, applyToAllItems: boolean) =>
                      updateMultiplePaintItems(quantity, applyToAllItems)
                    }
                    onNo={() => closePaintColorModal()}
                    errorMessage={paintModalErrorMessage}
                  />
                  <div className="container mt-2">
                    <div className={`${styles.headerNotAccordion} row`}>
                      <div className="col">
                        <h2>Shopping Cart</h2>
                      </div>
                      <div className="col col-lg-2">
                        <button
                          data-test="delete-all-items-btn"
                          className={`${styles.btn} w-100`}
                          type="button"
                          onClick={() => setShowDeleteAllItemsModal(true)}
                        >
                          Delete All Items
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <ul className={`${styles.cartItemList}`}>
                {/* ShoppingCartItem for ShoppingCartPage where we are creating a new sales order or new quote. */}
                {props.quoteId === undefined &&
                  props.items.map((item) => (
                    <ShoppingCartItem
                      key={item.cartItemId}
                      cartItemId={item.cartItemId}
                      name={item.name}
                      partNumber={item.partNumber}
                      paintColorId={item.paintColorId}
                      quantity={item.quantity}
                      quantityIsValid={item.quantityIsValid}
                      units={item.units}
                      price={item.price}
                      weight={item.weight}
                      companyDiscount={props.companyDiscount}
                      impellerTrim={item.impellerTrim}
                      impellerTrimIsValid={item.impellerTrimIsValid}
                      impellerAngle={item.impellerAngle}
                      impellerAngleIsValid={item.impellerAngleIsValid}
                      dateAvailable={item.dateAvailable}
                      isPaintable={item.isPaintable}
                      paintColors={props.paintColors}
                      isQuote={props.isQuote}
                      quoteId={props.quoteId}
                      onUpdateMultiplePaintCartItems={(itemId, newPaintColor) =>
                        handleMultiplePaintItems(itemId, newPaintColor)
                      }
                      onUpdateCartItem={(
                        itemId,
                        quantity,
                        quantityIsValid,
                        paintColorId,
                        impellerTrim,
                        impellerTrimIsValid,
                        impellerAngle,
                        impellerAngleIsValid
                      ) =>
                        parseUpdatedCart(
                          itemId,
                          quantity,
                          quantityIsValid,
                          paintColorId,
                          impellerTrim,
                          impellerTrimIsValid,
                          impellerAngle,
                          impellerAngleIsValid
                        )
                      }
                      onUpdateCartItemDateAvailable={(itemId, dateAvailable) =>
                        props.onUpdateCartItemDateAvailable(itemId, dateAvailable)
                      }
                      onUpdateQuoteCartItemDateAvailable={(itemId, dateAvailable) =>
                        props.onUpdateQuoteCartItemDateAvailable(itemId, dateAvailable)
                      }
                      onDeleteCartItem={() => setSelectedDeleteCartItemId(item.cartItemId)}
                    />
                  ))}
                {/* ShoppingCartItem for ShoppingCartPage for placing a sales order from an existing quote. */}
                {props.quoteId !== undefined &&
                  props.quoteCartItems.map((item) => (
                    <ShoppingCartItem
                      key={item.cartItemId}
                      cartItemId={item.cartItemId}
                      name={item.name}
                      partNumber={item.partNumber}
                      paintColorId={item.paintColorId}
                      quantity={item.quantity}
                      quantityIsValid={item.quantityIsValid}
                      units={item.units}
                      price={item.price}
                      weight={item.weight}
                      companyDiscount={props.companyDiscount}
                      impellerTrim={item.impellerTrim}
                      impellerTrimIsValid={item.impellerTrimIsValid}
                      impellerAngle={item.impellerAngle}
                      impellerAngleIsValid={item.impellerAngleIsValid}
                      dateAvailable={item.dateAvailable}
                      isPaintable={item.isPaintable}
                      paintColors={props.paintColors}
                      isQuote={props.isQuote}
                      quoteId={props.quoteId}
                      onUpdateMultiplePaintCartItems={(itemId, newPaintColor) =>
                        handleMultiplePaintItems(itemId, newPaintColor)
                      }
                      onUpdateCartItem={(
                        itemId,
                        quantity,
                        quantityIsValid,
                        paintColorId,
                        impellerTrim,
                        impellerTrimIsValid,
                        impellerAngle,
                        impellerAngleIsValid
                      ) =>
                        parseUpdatedCart(
                          itemId,
                          quantity,
                          quantityIsValid,
                          paintColorId,
                          impellerTrim,
                          impellerTrimIsValid,
                          impellerAngle,
                          impellerAngleIsValid
                        )
                      }
                      onUpdateCartItemDateAvailable={(itemId, dateAvailable) =>
                        props.onUpdateCartItemDateAvailable(itemId, dateAvailable)
                      }
                      onUpdateQuoteCartItemDateAvailable={(itemId, dateAvailable) =>
                        props.onUpdateQuoteCartItemDateAvailable(itemId, dateAvailable)
                      }
                      onDeleteCartItem={() => setSelectedDeleteCartItemId(item.cartItemId)}
                    />
                  ))}
              </ul>
              <div className={`${styles.cartFooter}`}>
                <div className="d-flex justify-content-end text-align-end pe-2 pt-1">
                  <div className="row">
                    <div className="col">
                      {listPrice !== null && listPrice !== 0 ? (
                        <Fragment>
                          {props.companyDiscount !== null ? (
                            <ul className={`${styles.priceListNotAccordion} pe-2 pt-1`} data-test="total-price-list">
                              {itemCount > 1 ? (
                                <li className={styles.priceCategory}>
                                  Subtotal ({itemCount} items):{" "}
                                  <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                                    $
                                    {listPrice.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </li>
                              ) : (
                                <li className={styles.priceCategory}>
                                  Subtotal ({itemCount} item):{" "}
                                  <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                                    $
                                    {listPrice.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </li>
                              )}
                              <li className={styles.priceCategory}>
                                {props.companyName} Discount:{" "}
                                <span className={styles.priceValue}>{props.companyDiscount}%</span>
                              </li>
                              <li className={styles.priceCategory}>
                                Total after discount:{" "}
                                <span className={styles.priceValue}>
                                  $
                                  {(listPrice - listPrice * (props.companyDiscount / 100)).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </li>
                              <li className={styles.priceCategory}>Applicable shipping and sales tax will be added</li>
                            </ul>
                          ) : (
                            <ul className={styles.priceList} data-test="price-list">
                              <li className={styles.priceCategory}>
                                Total:{" "}
                                <span data-test="price-list-number" className={styles.priceValue}>
                                  $
                                  {listPrice.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </li>
                              <li className={styles.priceCategory}>Applicable shipping and sales tax will be added</li>
                            </ul>
                          )}
                        </Fragment>
                      ) : (
                        <Fragment></Fragment>
                      )}
                    </div>
                  </div>
                </div>
                {props.distributorAccountCode !== null ? (
                  <Fragment>{props.children}</Fragment>
                ) : (
                  <div className={`${styles.noDistributorMessage}`}>
                    Please contact Cornell Pump to associate your company with an approved distributor account.
                  </div>
                )}
              </div>
            </div> // If the cart is empty display a message.
          ) : (
            <div>
              <div className={`${styles.cartContainer} container mt-4`}>
                <div className={`${styles.headerNotAccordion} p-3 mt-3`}>
                  <div className="row align-items-top">
                    <div className="col">
                      <div className={`${styles.title} row`}>
                        <div className="col">Shopping Cart</div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className={styles.emptyShoppingCart}>{props.loading ? "" : "Your shopping cart is empty."}</p>
              </div>
            </div>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}

interface Props {
  items: CartItem[];
  quoteCartItems: QuoteCartItemShoppingCart[];
  previousQuoteCartItems: QuoteCartItemShoppingCart[];
  paintColors: PaintColor[];
  companyName: string | null;
  companyDiscount: number | null;
  accordionView: boolean;
  distributorAccountCode: string | null;
  loading: boolean;
  isQuote: boolean;
  quoteId: string | undefined;
  errorMessage: string;
  children: Component | null;
  onUpdateMultiplePaintCartItems: (itemId: number, newPaintColorId: number | null, newQuantity: string) => void;
  onUpdateCartItem: (
    itemId: number,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) => void;
  onUpdateCartItemDateAvailable: (itemId: number, dateAvailable: string | null) => void;
  onUpdateQuoteCartItemDateAvailable: (itemId: number, dateAvailable: string | null) => void;
  onDeleteCartItem: (itemId: number | null) => void;
  onDeleteAllCartItems: () => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface CartItem {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
}

interface QuoteCartItemShoppingCart {
  cartItemId: number;
  name: string; //Description of the part.
  partId: number;
  partNumber: string;
  isPaintable: boolean;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
}
